import React, { useState } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';

const YudielQRScanner = ({ onScanResult, onError, className = '' }) => {
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);

  const handleScan = (result) => {
    console.log('YudielQRScanner: Raw scan result:', result);
    
    if (result && result.length > 0) {
      // The result format may vary, try different possible formats
      const qrData = result[0]?.rawValue || result[0]?.data || result[0] || result;
      console.log('YudielQRScanner: QR Code detected:', qrData);
      
      if (qrData && onScanResult) {
        onScanResult(typeof qrData === 'string' ? qrData : JSON.stringify(qrData));
      }
    } else if (result && typeof result === 'string') {
      // If result is directly a string
      console.log('YudielQRScanner: QR Code detected (string):', result);
      if (onScanResult) {
        onScanResult(result);
      }
    }
  };

  const handleError = (error) => {
    console.error('YudielQRScanner: Error:', error);
    
    let errorMessage = 'Scanner error occurred';
    
    if (error.name === 'NotAllowedError') {
      errorMessage = 'Camera permission denied. Please allow camera access and refresh the page.';
      setHasPermission(false);
    } else if (error.name === 'NotFoundError') {
      errorMessage = 'No camera found. Make sure your device has a camera.';
    } else if (error.name === 'NotSupportedError') {
      errorMessage = 'Camera not supported. Make sure you\'re using HTTPS or localhost.';
    } else if (error.name === 'NotReadableError') {
      errorMessage = 'Camera is being used by another application. Please close other apps using the camera.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    setIsActive(false);
    
    if (onError) {
      onError(new Error(errorMessage));
    }
  };

  const startScanner = () => {
    console.log('YudielQRScanner: Starting scanner...');
    setError(null);
    setIsActive(true);
    setHasPermission(null);
  };

  const stopScanner = () => {
    console.log('YudielQRScanner: Stopping scanner...');
    setIsActive(false);
    setError(null);
  };

  return (
    <div className={`yudiel-qr-scanner ${className}`}>
      <div className="scanner-container">
        {error && (
          <div className="error-message mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <div className="font-semibold">Scanner Error</div>
            <div className="mt-1">{error}</div>
            {hasPermission === false && (
              <div className="mt-2 text-sm">
                <strong>Camera Permission Help:</strong>
                <ul className="list-disc list-inside mt-1">
                  <li><strong>Safari on iOS:</strong> Settings → Safari → Camera → Allow</li>
                  <li><strong>Chrome:</strong> Click the camera icon in the address bar</li>
                  <li>Refresh this page after changing settings</li>
                  <li>Try using a private/incognito tab</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {!isActive && (
          <div className="text-center mb-4">
            <button
              onClick={startScanner}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center mx-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Enable Camera
            </button>
            <div className="mt-2 text-sm text-gray-600">
              Click to start QR code scanning
            </div>
          </div>
        )}

        {isActive && (
          <div className="scanner-active">
            <div className="relative bg-black rounded-lg overflow-hidden">
              <Scanner
                onScan={handleScan}
                onError={handleError}
                constraints={{
                  facingMode: 'environment', // Use back camera
                }}
                allowMultiple={false}
                scanDelay={300}
                styles={{
                  container: {
                    width: '100%',
                    maxWidth: '500px',
                    margin: '0 auto'
                  },
                  video: {
                    width: '100%',
                    height: 'auto',
                    minHeight: '300px',
                    maxHeight: '500px',
                    objectFit: 'cover'
                  }
                }}
              />
              
              {/* Scan overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-48 h-48 border-2 border-green-400 rounded-lg opacity-75">
                    {/* Corner markers */}
                    <div className="absolute -top-1 -left-1 w-8 h-8 border-l-4 border-t-4 border-green-400 rounded-tl-lg"></div>
                    <div className="absolute -top-1 -right-1 w-8 h-8 border-r-4 border-t-4 border-green-400 rounded-tr-lg"></div>
                    <div className="absolute -bottom-1 -left-1 w-8 h-8 border-l-4 border-b-4 border-green-400 rounded-bl-lg"></div>
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 border-r-4 border-b-4 border-green-400 rounded-br-lg"></div>
                  </div>
                </div>
              </div>
              
              {/* Status indicator */}
              <div className="absolute bottom-4 left-4 right-4 text-center">
                <div className="bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg">
                  <div className="flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                    Scanner Active - Point camera at QR code
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-4">
              <button
                onClick={stopScanner}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Stop Scanner
              </button>
            </div>
          </div>
        )}
        
        <div className="mt-4 text-sm text-gray-600 text-center">
          <div>✓ Compatible with Safari, Chrome, Firefox, and Edge</div>
          <div>✓ Works on iOS, Android, and Desktop</div>
          <div>Make sure QR codes are well-lit and clearly visible</div>
        </div>
      </div>
    </div>
  );
};

export default YudielQRScanner;
